// src/components/home/HorizontalScroller.jsx
import React from 'react';
import styled from 'styled-components';
import { BP } from '../../theme/breakpoints';

const NARROW_BREAKPOINT = 700;
const MIN_VIEWPORT_CARD_WIDTH = 200;
const MAX_VIEWPORT_CARD_WIDTH = 320;
const MOBILE_CONTAINER_MAX = 340;

function getViewportWidth(fallback = Infinity) {
  if (typeof window === 'undefined') return fallback;
  const { innerWidth } = window;
  const docWidth = document?.documentElement?.clientWidth;
  if (Number.isFinite(innerWidth) && innerWidth > 0) {
    return docWidth ? Math.min(innerWidth, docWidth) : innerWidth;
  }
  return docWidth || fallback;
}

const Wrap = styled.div`
  width: 100%;
`;
const Rail = styled.div`
  position: relative;
  border: 1px solid var(--bdr);
  background: var(--card);
  border-radius: 16px;
  box-shadow: var(--shadow);
  overflow: hidden;
  isolation: isolate;
  max-width: ${({ $maxWidth }) => ($maxWidth ? (typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth) : 'none')};
  margin: ${({ $centered }) => ($centered ? '16px auto' : '16px 0')};
  padding: ${({ $pad }) => `${$pad}px`};

  /* Средние экраны - ограничиваем ширину под 3 колонки */
  @media (min-width: 640px) and (max-width: 1429px) {
    max-width: min(96vw, 1000px);
  }

  @media (max-width: ${NARROW_BREAKPOINT}px) {
    max-width: ${MOBILE_CONTAINER_MAX}px;
    margin: ${({ $centered }) => ($centered ? '16px auto' : '16px 0')};
  }
`;
const Row = styled.div`
  display: grid;
  width: 100%;
  grid-auto-flow: column;
  grid-auto-columns: var(--col-width, 280px);
  gap: ${({ $gap }) => `${$gap}px`};
  align-items: start; /* Карточки не растягиваются по высоте */

  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;

  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  > * { scroll-snap-align: start; }

  /* Мобильные устройства - 1 слайд на всю ширину */
  @media (max-width: ${NARROW_BREAKPOINT}px) {
    grid-auto-columns: 100%;
    > * {
      width: 100%;
      max-width: ${MAX_VIEWPORT_CARD_WIDTH}px;
      margin: 0 auto;
    }
  }
  
  /* Средние экраны (640px - 1429px) - принудительно 3 колонки */
  @media (min-width: 640px) and (max-width: 1429px) {
    grid-auto-columns: calc((100% - ${({ $gap }) => $gap * 2}px) / 3);
    > * {
      width: 100%;
    }
  }
`;

/* статичные круглые стрелки внутри рамки */
const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%) !important;
  width: 40px; height: 40px; border-radius: 50%;
  border: 1px solid var(--bdr);
  background: rgba(255,255,255,.90);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #111;
  box-shadow: 0 6px 20px rgba(0,0,0,.12);
  display: grid; place-items: center;
  cursor: pointer; z-index: 2;
  transition: background .2s ease; /* убрали тени/границы, чтобы не было подрагивания */
  will-change: transform;

  &:hover{ transform: translateY(-50%) !important; background: rgba(255,255,255,.96); }
  &:active{ transform: translateY(-50%) !important; }

  @media (max-width:720px){ display:none; }
`;
const Prev = styled(ArrowBtn)` left: 6px; `;
const Next = styled(ArrowBtn)` right: 6px; `;

const Icon = styled.svg` width:20px; height:20px; display:block; `;
const ArrowLeft = () => (
  <Icon viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14.5 4.5L7.5 12l7 7.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Icon>
);
const ArrowRight = () => (
  <Icon viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9.5 4.5L16.5 12l-7 7.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Icon>
);

export default function HorizontalScroller({
  children,
  /* компактная сетка: 4 — десктоп, 3 — планшет/моб, 2 — совсем узкие */
  perViewDesktop = 4,
  perViewTablet  = 3,
  perViewMobile  = 2,
  perViewTiny    = 1,

  /* размеры карточек — делаем аккуратно мельче */
  itemMin = 220,
  itemMax = 280,

  /* коробка */
  centered = true,
  maxWidth = 980,      // число(px) или строка (например '100%')
  pad = 12,
  gap = 12,
  limitDesktopWidth = false, // по умолчанию не ограничиваем, чтобы избежать «подпрыгивания»
}) {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    const rail = el?.parentElement;
    if (!el || !rail) return;

    let raf = 0;
    let lastCol = -1;
    let initDone = false;

    const measureWidth = () => {
      // ширина внутренней области рамки (без паддингов)
      const railRect = rail.getBoundingClientRect();
      const style = getComputedStyle(rail);
      const padL = parseFloat(style.paddingLeft || '0');
      const padR = parseFloat(style.paddingRight || '0');
      return Math.max(0, railRect.width - padL - padR);
    };

    const fitWidth = (w, v) => {
      if (!v) return itemMin;
      const totalGap = gap * Math.max(0, v - 1);
      const raw = Math.floor((w - totalGap) / v);
      if (!Number.isFinite(raw) || raw <= 0) {
        return Math.min(itemMax, Math.max(itemMin, 0));
      }
      const lowerBound = raw < itemMin ? raw : itemMin;
      return Math.max(lowerBound, Math.min(itemMax, raw));
    };

    const computeCol = (w) => {
      const viewport = getViewportWidth(w);
      
      // Мобильные устройства - специальная обработка
      if (viewport < NARROW_BREAKPOINT) {
        const target = Math.min(
          Math.max(viewport - pad * 2, MIN_VIEWPORT_CARD_WIDTH),
          MAX_VIEWPORT_CARD_WIDTH
        );
        return Math.max(itemMin, target);
      }
      
      // Очень маленькие экраны (до 360px) - 1 слайд
      if (w < 360) return fitWidth(w, Math.max(1, perViewTiny));
      
      // Маленькие экраны (360px - 640px) - 2 слайда
      if (w < 640) return fitWidth(w, perViewMobile);
      
      // СРЕДНИЕ ЭКРАНЫ (640px - 1429px) - ВСЕГДА 3 слайда
      if (w >= 640 && w < 1430) {
        // Принудительно вычисляем ширину для 3 колонок
        const totalGap = gap * 2; // 2 промежутка между 3 колонками
        const colWidth = Math.floor((w - totalGap) / 3);
        return Math.max(itemMin, Math.min(itemMax, colWidth));
      }
      
      // Десктоп (1430px+) - 4 слайда
      const maxW = itemMax * perViewDesktop + gap * (perViewDesktop - 1);
      const effectiveW = Math.min(w, maxW);
      return fitWidth(effectiveW, perViewDesktop);
    };

    const scheduleApply = (w, { resetOnce = false } = {}) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const col = computeCol(w);
        if (col === lastCol) return;
        el.style.setProperty('--col-width', `${col}px`);
        lastCol = col;

        // Ограничиваем ширину контейнера только когда хотим фиксированное число колонок на десктопе (обычно 4)
        if (limitDesktopWidth && perViewDesktop >= 4 && w >= BP.desktop) {
          const maxContainerW = itemMax * perViewDesktop + gap * (perViewDesktop - 1) + pad * 2;
          rail.style.maxWidth = `${maxContainerW}px`;
        } else {
          rail.style.maxWidth = '';
        }

        if (resetOnce && !initDone) {
          el.scrollLeft = 0;
          initDone = true;
        }
      });
    };

    scheduleApply(measureWidth(), { resetOnce: true });

    const ro = new ResizeObserver(() => scheduleApply(measureWidth()));

    try { ro.observe(rail, { box: 'border-box' }); } catch { ro.observe(rail); }

    const onOrient = () => scheduleApply(measureWidth());
    window.addEventListener('orientationchange', onOrient, { passive: true });
    window.addEventListener('resize', onOrient, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('orientationchange', onOrient);
      window.removeEventListener('resize', onOrient);
    };
  }, [perViewDesktop, perViewTablet, perViewMobile, perViewTiny, itemMin, itemMax, gap, pad, limitDesktopWidth]);

  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    
    // Получаем ширину одной карточки из CSS переменной
    const cardWidth = parseFloat(getComputedStyle(el).getPropertyValue('--col-width')) || 280;
    const scrollAmount = cardWidth + gap; // ширина карточки + промежуток
    
    if (dir > 0) {
      // Прокрутка вправо - просто прокручиваем
      el.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    } else {
      // Прокрутка влево - просто прокручиваем
      el.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <Wrap aria-roledescription="carousel">
      <Rail $maxWidth={maxWidth} $centered={centered} $pad={pad}>
        <Prev onClick={() => scroll(-1)} aria-label="Prev"><ArrowLeft/></Prev>
        <Next onClick={() => scroll(1)} aria-label="Next"><ArrowRight/></Next>
        <Row ref={ref} role="group" $gap={gap}>{children}</Row>
      </Rail>
    </Wrap>
  );
}
